<template>
  <BaseDrawer :is-open="isOpen" shrink @close="handleClose">
    <template #title> Log In As Supervisor </template>
    <form
      class="mt-4 flex h-full flex-col items-center justify-end pb-6"
      @submit="onSubmit"
    >
      <PinInput
        :model-value="formValues.pin"
        @update:model-value="setFieldValue('pin', $event)"
      />
    </form>
  </BaseDrawer>
</template>

<script setup lang="ts">
import { logInAsSupervisor } from "@/services/logInAsSupervisor"

import z from "zod"
import { useForm } from "vee-validate"
import { toFormValidator } from "@vee-validate/zod"

const { setElevatedToken } = useAuth()

const { startLoading, stopLoading } =
  usePageBlockingLoading()

const {
  reveal,
  isRevealed: isOpen,
  confirm,
  cancel,
  onReveal,
} = useSupervisorLoginDrawer()

defineExpose({
  reveal,
})

onReveal(() => {
  handleFormReset()
})

const handleClose = () => cancel()

const pinFormSchema = z.object({
  pin: z
    .string()
    .length(4, { message: "Pin must be 4 characters long" })
    .regex(/\d+/, {
      message: "Pin must only contain numeric characters",
    }),
})

const {
  handleSubmit,
  handleReset: handleFormReset,
  setFieldValue,
  values: formValues,
} = useForm({
  validationSchema: toFormValidator(pinFormSchema),
  initialValues: {
    pin: "",
  },
})

const onSubmit = handleSubmit(async (values) => {
  const payload = values
  startLoading()
  try {
    const body = await logInAsSupervisor(payload)
    setElevatedToken(body.accessToken)
    confirm()
  } catch {
    handleFormReset()
    toast.error(
      "Failed to log in as supervisor, please check your pin"
    )
  } finally {
    stopLoading()
  }
})

const pinIsFullyInput = computed(
  () => (formValues.pin?.length ?? 0) === 4
)

whenever(
  () => pinIsFullyInput.value,
  () => onSubmit()
)
</script>
